import { useMemo } from "react";
import { bool, object, number } from "prop-types";
import { observer } from "mobx-react";
import { Tables, hashCode } from "@repo/utils";
import { chunk } from "lodash";
import { ELEMENT_TYPES, getTimelineChartSegments } from "@utils";
import {
  useEstimateStructure,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  useTableExpandHandler,
  Row,
  TimelineValueCell,
  RowGroup,
  ExpandContainer,
} from "@tools";
import { Collapse } from "@material-ui/core";
import { TimelineFiller } from "../TimelineFiller";
import { TimelineRowGroupList } from "../TimelineRowGroupList/TimelineRowGroupList";
import { TitleCell, TimelineNewChildRow } from "./components";
import classnames from "classnames";

export const TimelineRowGroup = observer(({
  workType,
  tableDepth,
}) => {
  const structure = useEstimateStructure();
  const { isProposal, allowEdition, blockExpansions } = useEstimateEditorSettings();
  const { useExpandCell } = useEstimateTableSettings();
  
  const { modifier } = structure.settings;

  const {
    id,
    treePath,
    color,
    isTimelineOpened,
    presentedTimeline,
    resources,
    displayTimelineResources,
    turnOff,
    usesTimelinePercent,
    maxTimelineHours,
    timelineStartMonth,
    parsedMta,
    isHidden,
    hideOnTimeline,
  } = workType;
  
  const className = useVisibilityModeCellClassName(isHidden || hideOnTimeline);
  
  const visibleResources = useMemo(() => (
    isProposal
      ? displayTimelineResources
      : resources || []
  ), [isProposal, displayTimelineResources, resources]);
  
  const chunkedTimeline = chunk(presentedTimeline?.weeks || [], 4).slice(0, workType.totalDevelopmentTime);
  
  const timelineChartSegments = getTimelineChartSegments(
    presentedTimeline,
    parsedMta[structure.timelineWorkTypeHoursKey(id)],
    modifier,
    usesTimelinePercent
  );

  const expandable = useMemo(() => (
    allowEdition || visibleResources.length
  ), [allowEdition, visibleResources]);
  
  const expanded = useMemo(() => (
    expandable && (isTimelineOpened || blockExpansions)
  ), [isTimelineOpened, blockExpansions, expandable]);
  
  const displayCellContent = useMemo(() => (
    !expanded || !visibleResources.length
  ), [expanded, visibleResources]);
  
  const maxHours = useMemo(() => (
    maxTimelineHours / modifier
  ), [maxTimelineHours, modifier]);
  
  const hashedPath = useMemo(() => (
    [Tables.TIMELINE, ...treePath].map((id) => hashCode(id))
  ), [treePath]);

  const handleResourceReorder = (list) =>
    workType.reorderResources(list);
  
  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: expandable,
    isOpened: isTimelineOpened,
    onExpand: workType.setTimelineOpenState
  });
  
  return (
    <RowGroup
      tableDepth={tableDepth}
      isActive={expanded}
      hashedPath={hashedPath}
      isStatic={!expandable}
      elementType={ELEMENT_TYPES.TIMELINE_WORK}
      presetClass="preset-accentTimeline"
      className={classnames("timeline", className)}
    >
      <Row
        // useExpandCell={useExpandCell}
        useDragHandleCell={allowEdition}
        expandOnClick
        onExpand={handleOpen}
        expandable={expandable}
        expanded={expanded}
        isFirstGroupRow
        isLastGroupRow={!expanded}
        tableDepth={tableDepth}
        // useFiller
        visible={!turnOff}
        presetClass={ expanded ? "preset-activeTimeline" : "preset-rowTimeline" }
        actionPresetClass="preset-paperTimeline"
      >
        <TitleCell
          workType={workType}
          tableDepth={tableDepth}
          isOpened={expanded}
        >
          {
            useExpandCell &&
            <ExpandContainer
              tableDepth={tableDepth}
              expandable={expandable}
              expanded={expanded}
            />
          }
        </TitleCell>
        {
          chunkedTimeline.map((month, i) => (
            <TimelineValueCell
              key={`month${i}`}
              monthIndex={i}
              month={month}
              maxHours={maxHours}
              timelineStarted={!timelineStartMonth || i >= timelineStartMonth }
              readOnly={ Boolean(visibleResources.length || usesTimelinePercent) }
              displayCellContent={displayCellContent}
              element={workType}
              color={color}
              timelineChartSegments={timelineChartSegments}
            />
          ))
        }
        <TimelineFiller totalDevelopmentTime={chunkedTimeline.length} />
      </Row>
      <Collapse
        in={expanded}
        className="w-full"
        mountOnEnter
        unmountOnExit
        data-id={hashCode(`${id}_Tcont`)}
        data-p={treePath.join("/")}
        data-t={ELEMENT_TYPES.TIMELINE_RESOURCE}
        { ...collapseProps }
      >
        <TimelineRowGroupList
          listElements={visibleResources}
          onReorder={handleResourceReorder}
          isLastGroupRow={!allowEdition}
          tableDepth={tableDepth}
          turnOff={turnOff}
          parentColor={color}
          parentUsesTimelinePercent={usesTimelinePercent}
          disabled={!allowEdition}
          hashedPath={hashedPath}
        />
        {
          allowEdition &&
          <TimelineNewChildRow workType={workType} tableDepth={tableDepth} />
        }
      </Collapse>
    </RowGroup>
  );
});

TimelineRowGroup.propTypes = {
  workType: object.isRequired,
  tableDepth: number.isRequired,
  isLastGroupRow: bool,
};